import { useEffect, useRef, useState } from "react";
import { getApiRoot } from "../utils/getApiRoot";
import { Newspaper, RotateCcw } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import NewsFeedView from "./news/NewsFeedView";

const ACCENT = "#fcb14a";
const API_ROOT = getApiRoot();

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  EMPTY: "empty",
  ERROR: "error",
};

function buildNewsUrl({ limit, lang = "es-ES", q = "", sources = [], contentLang = "all" }) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("lang", lang);
  params.set("includeSteam", "1");
  const normalizedContentLang = String(contentLang || "").trim().toLowerCase();
  if (normalizedContentLang === "es" || normalizedContentLang === "en") {
    params.set("contentLang", normalizedContentLang);
  }
  const query = String(q || "").trim();
  if (query) params.set("q", query);
  const selectedSources = Array.isArray(sources) ? sources.filter(Boolean) : [];
  if (selectedSources.length) params.set("sources", selectedSources.join(","));

  if (!API_ROOT) return `/api/news/latest?${params.toString()}`;
  const path = API_ROOT.endsWith("/api") ? `${API_ROOT}/news/latest` : `${API_ROOT}/api/news/latest`;
  return `${path}?${params.toString()}`;
}

function canonicalizeUrl(raw = "") {
  try {
    const parsed = new URL(String(raw || "").trim());
    if (!/^https?:$/.test(parsed.protocol)) return "";
    parsed.hash = "";
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id", "fbclid", "gclid"].forEach(
      (param) => parsed.searchParams.delete(param)
    );
    return parsed.toString();
  } catch {
    return "";
  }
}

function sanitizeMediaUrl(raw = "") {
  try {
    const parsed = new URL(String(raw || "").trim());
    if (!/^https?:$/.test(parsed.protocol)) return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function normalizeTitle(value = "") {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function decodeTextEntities(value = "") {
  const namedEntities = {
    Aacute: "Á",
    aacute: "á",
    Eacute: "É",
    eacute: "é",
    Iacute: "Í",
    iacute: "í",
    Oacute: "Ó",
    oacute: "ó",
    Uacute: "Ú",
    uacute: "ú",
    Ntilde: "Ñ",
    ntilde: "ñ",
    Uuml: "Ü",
    uuml: "ü",
    Ccedil: "Ç",
    ccedil: "ç",
    agrave: "à",
    egrave: "è",
    igrave: "ì",
    ograve: "ò",
    ugrave: "ù",
    acirc: "â",
    ecirc: "ê",
    icirc: "î",
    ocirc: "ô",
    ucirc: "û",
  };

  function fromCodePointSafe(codePoint) {
    if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) return "";
    try {
      return String.fromCodePoint(codePoint);
    } catch {
      return "";
    }
  }

  return String(value || "")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => fromCodePointSafe(parseInt(hex, 16)))
    .replace(/&#([0-9]+);/g, (_, dec) => fromCodePointSafe(parseInt(dec, 10)))
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&ndash;/g, "–")
    .replace(/&mdash;/g, "—")
    .replace(/&hellip;/g, "…")
    .replace(/&lsquo;/g, "‘")
    .replace(/&rsquo;/g, "’")
    .replace(/&ldquo;/g, "“")
    .replace(/&rdquo;/g, "”")
    .replace(/&([a-zA-Z]+);/g, (full, key) => namedEntities[key] || full)
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function dedupeNewsItems(items = []) {
  const sorted = [...items].sort((a, b) => {
    const aTs = Date.parse(a.date || "") || 0;
    const bTs = Date.parse(b.date || "") || 0;
    return bTs - aTs;
  });

  const seenUrls = new Set();
  const seenTitles = new Set();
  const deduped = [];

  for (const item of sorted) {
    const urlKey = canonicalizeUrl(item.url);
    const titleKey = normalizeTitle(item.title);
    const day = String(item.date || "").slice(0, 10) || "na";
    const titleDayKey = titleKey ? `${titleKey}|${day}` : "";

    if (urlKey && seenUrls.has(urlKey)) continue;
    if (titleDayKey && seenTitles.has(titleDayKey)) continue;

    if (urlKey) seenUrls.add(urlKey);
    if (titleDayKey) seenTitles.add(titleDayKey);

    deduped.push(item);
  }

  return deduped;
}

function fmtDate(value) {
  try {
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

function fmtRelativeDate(value) {
  try {
    const target = Date.parse(String(value || ""));
    if (!Number.isFinite(target)) return "";
    const deltaMs = Date.now() - target;
    if (deltaMs < 0) return "Ahora";
    const mins = Math.floor(deltaMs / 60_000);
    if (mins < 60) return `hace ${Math.max(1, mins)} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `hace ${days} d`;
    return fmtDate(value);
  } catch {
    return "";
  }
}

const CARD_THEMES = [
  {
    accent: "#38bdf8",
    border: "rgba(56,189,248,0.35)",
    glow: "rgba(56,189,248,0.24)",
    bgTop: "rgba(56,189,248,0.18)",
    bgBottom: "rgba(15,23,42,0.78)",
  },
  {
    accent: "#f59e0b",
    border: "rgba(245,158,11,0.38)",
    glow: "rgba(245,158,11,0.22)",
    bgTop: "rgba(245,158,11,0.18)",
    bgBottom: "rgba(38,24,10,0.8)",
  },
  {
    accent: "#22c55e",
    border: "rgba(34,197,94,0.35)",
    glow: "rgba(34,197,94,0.2)",
    bgTop: "rgba(34,197,94,0.15)",
    bgBottom: "rgba(10,35,22,0.82)",
  },
  {
    accent: "#a78bfa",
    border: "rgba(167,139,250,0.34)",
    glow: "rgba(167,139,250,0.2)",
    bgTop: "rgba(167,139,250,0.15)",
    bgBottom: "rgba(24,16,44,0.82)",
  },
  {
    accent: "#f43f5e",
    border: "rgba(244,63,94,0.33)",
    glow: "rgba(244,63,94,0.2)",
    bgTop: "rgba(244,63,94,0.16)",
    bgBottom: "rgba(45,12,22,0.84)",
  },
];

function hashSource(value = "") {
  const text = String(value || "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

function themeForSource(source = "") {
  return CARD_THEMES[hashSource(source) % CARD_THEMES.length];
}

function normalizeApiItems(rawItems = []) {
  const mapped = rawItems
    .map((item, index) => {
      const sourceType = String(item?.sourceType || "rss");
      const appid = item?.appid;
      const gid = item?.gid;
      const isSteamItem = sourceType === "steam" && appid && gid;
      const safeUrl = canonicalizeUrl(item?.url || "");
      const title = decodeTextEntities(item?.title || "");
      if (!title) return null;
      if (!safeUrl && !isSteamItem) return null;
      const safeImageUrl = sanitizeMediaUrl(item?.imageUrl || item?.image || "");

      return {
        id:
          item?.id ||
          `${sourceType}:${String(appid || "na")}:${String(gid || "na")}:${safeUrl || normalizeTitle(title) || index}`,
        source: decodeTextEntities(item?.source || "Fuente externa"),
        sourceType,
        title,
        date: item?.date || null,
        snippet: decodeTextEntities(item?.snippet || ""),
        url: safeUrl,
        imageUrl: safeImageUrl,
        appid,
        gid,
      };
    })
    .filter(Boolean);

  return dedupeNewsItems(mapped);
}

function Title({ linkTo = "" }) {
  const titleContent = (
    <>
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          background: "#000",
          border: "1px solid #222",
          borderRadius: 8,
        }}
      >
        <Newspaper size={22} color={ACCENT} />
      </span>
      Últimas noticias gaming
    </>
  );

  if (linkTo) {
    return (
      <Link
        to={linkTo}
        style={{ textDecoration: "none", color: "inherit" }}
        aria-label="Abrir pagina completa de noticias gaming"
      >
        <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
          {titleContent}
        </h2>
      </Link>
    );
  }

  return (
    <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
      {titleContent}
    </h2>
  );
}

export default function SteamNewsFeed({
  presentation = "embedded",
  limit = 12,
  lang = "es-ES",
  enableFilters = false,
  initialQuery = "",
  initialSource = "all",
  initialLanguage = "",
  showTitle = true,
  titleLinkTo = "",
  showViewAllLink = false,
  viewAllTo = "/news",
  highlightLead = false,
  dashboardOverlayFixPx = 0,
}) {
  const normalizedInitialLanguage = String(initialLanguage || "all")
    .trim()
    .toLowerCase();
  const defaultLanguage =
    normalizedInitialLanguage === "es" || normalizedInitialLanguage.startsWith("es-")
      ? "es"
      : normalizedInitialLanguage === "en" || normalizedInitialLanguage.startsWith("en-")
        ? "en"
        : "all";
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMsg, setErrorMsg] = useState("");
  const [degradedMsg, setDegradedMsg] = useState("");
  const [availableSources, setAvailableSources] = useState([]);
  const [searchInput, setSearchInput] = useState(String(initialQuery || ""));
  const [debouncedSearch, setDebouncedSearch] = useState(String(initialQuery || "").trim());
  const [selectedSource, setSelectedSource] = useState(initialSource || "all");
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [reloadToken, setReloadToken] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const tapRef = useRef({
    startX: 0,
    startY: 0,
    startT: 0,
    moved: false,
    lastTouchNavAt: 0,
  });

  const openItem = (item) => {
    const isSteamItem = item?.sourceType === "steam" && item?.appid && item?.gid;
    if (isSteamItem) {
      navigate(`/news/${item.appid}/${item.gid}`, { state: { background: location } });
      return;
    }

    if (item?.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    tapRef.current.startX = t.clientX;
    tapRef.current.startY = t.clientY;
    tapRef.current.startT = Date.now();
    tapRef.current.moved = false;
  };

  const handleTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - tapRef.current.startX);
    const dy = Math.abs(t.clientY - tapRef.current.startY);
    if (dx > 8 || dy > 8) tapRef.current.moved = true;
  };

  const handleTouchEnd = (e, item) => {
    const dt = Date.now() - tapRef.current.startT;
    if (!tapRef.current.moved && dt < 300) {
      tapRef.current.lastTouchNavAt = Date.now();
      e.preventDefault();
      e.stopPropagation();
      openItem(item);
    }
  };

  const handleClick = (e, item) => {
    if (Date.now() - tapRef.current.lastTouchNavAt < 500) {
      e.preventDefault();
      return;
    }
    openItem(item);
  };

  useEffect(() => {
    if (!enableFilters) return;
    setSearchInput(String(initialQuery || ""));
    setDebouncedSearch(String(initialQuery || "").trim());
  }, [initialQuery, enableFilters]);

  useEffect(() => {
    if (!enableFilters) return;
    setSelectedSource(initialSource || "all");
  }, [initialSource, enableFilters]);

  useEffect(() => {
    if (!enableFilters) return;
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage, enableFilters]);

  useEffect(() => {
    if (!enableFilters) {
      setDebouncedSearch("");
      return undefined;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(String(searchInput || "").trim());
    }, 260);
    return () => clearTimeout(timer);
  }, [searchInput, enableFilters]);

  const triggerRetry = () => {
    setIsRetrying(true);
    setReloadToken((prev) => prev + 1);
  };

  useEffect(() => {
    let cancelled = false;

    async function fetchNews() {
      try {
        setStatus(STATUS.LOADING);
        setErrorMsg("");
        setDegradedMsg("");

        const selectedSources =
          enableFilters && selectedSource && selectedSource !== "all" ? [selectedSource] : [];
        const resolvedLang =
          selectedLanguage === "en" ? "en-US" : selectedLanguage === "es" ? "es-ES" : lang;
        const url = buildNewsUrl({
          limit,
          lang: enableFilters ? resolvedLang : lang,
          q: enableFilters ? debouncedSearch : "",
          sources: selectedSources,
          contentLang: enableFilters ? selectedLanguage : "all",
        });
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Backend ${response.status}`);
        const data = await response.json();

        const normalized = normalizeApiItems(Array.isArray(data?.items) ? data.items : []);

        if (cancelled) return;

        if (data?.stale || data?.fallback === "stale-cache") {
          setDegradedMsg(
            "Algunas fuentes no respondieron. Te mostramos noticias guardadas mientras se actualiza."
          );
        } else if (data?.degraded) {
          setDegradedMsg("Algunas fuentes están tardando. Te mostramos las noticias disponibles.");
        }

        const sourcesFromApi = Array.isArray(data?.availableSources)
          ? data.availableSources.filter(Boolean)
          : [];
        if (sourcesFromApi.length) {
          setAvailableSources(sourcesFromApi);
        } else {
          const inferred = Array.from(new Set(normalized.map((item) => item.source).filter(Boolean))).sort(
            (a, b) => a.localeCompare(b, "es", { sensitivity: "base" })
          );
          setAvailableSources(inferred);
        }

        setItems(normalized);
        setStatus(normalized.length ? STATUS.SUCCESS : STATUS.EMPTY);
      } catch (error) {
        console.error("[NewsFeed] error:", error);
        if (cancelled) return;
        setItems([]);
        setAvailableSources([]);
        setStatus(STATUS.ERROR);
        setErrorMsg("No se pudieron cargar noticias. Revisa tu conexión o inténtalo de nuevo.");
      } finally {
        if (!cancelled) setIsRetrying(false);
      }
    }

    fetchNews();
    return () => {
      cancelled = true;
    };
  }, [limit, lang, reloadToken, debouncedSearch, selectedSource, selectedLanguage, enableFilters]);

  const hasActiveFilters =
    enableFilters &&
    (String(debouncedSearch || "").trim().length > 0 ||
      selectedSource !== "all" ||
      selectedLanguage !== "all");

  if (presentation === "page") {
    return (
      <NewsFeedView
        items={items}
        status={status}
        errorMsg={errorMsg}
        degradedMsg={degradedMsg}
        availableSources={availableSources}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        selectedSource={selectedSource}
        setSelectedSource={setSelectedSource}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        hasActiveFilters={hasActiveFilters}
        triggerRetry={triggerRetry}
        location={location}
        formatDate={fmtDate}
        clearFilters={() => {
          setSearchInput("");
          setDebouncedSearch("");
          setSelectedSource("all");
          setSelectedLanguage("all");
        }}
      />
    );
  }

  return (
    <section style={{ marginTop: 36 }} className="xp-lets" aria-live="polite">
      {showTitle && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <Title linkTo={titleLinkTo} />
          {showViewAllLink && (
            <Link
              to={viewAllTo}
              style={{
                color: "#00b3ff",
                textDecoration: "none",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              Ver todas
            </Link>
          )}
        </div>
      )}

      {enableFilters && (
        <div
          style={{
            display: "grid",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por palabra clave (ej: half-life 3)"
              aria-label="Buscar noticias por palabra clave"
              style={{
                minWidth: 260,
                flex: 1,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 10,
                color: "#f5f5f5",
                padding: "10px 12px",
                fontSize: 14,
              }}
            />

            <div
              role="group"
              aria-label="Filtrar noticias por idioma"
              style={{
                display: "inline-flex",
                alignItems: "center",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 10,
                overflow: "hidden",
                background: "#151515",
              }}
            >
              {[
                { value: "all", label: "Ambos" },
                { value: "es", label: "Español" },
                { value: "en", label: "English" },
              ].map((option) => {
                const active = selectedLanguage === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedLanguage(option.value)}
                    style={{
                      border: "none",
                      borderRight:
                        option.value === "en" ? "none" : "1px solid rgba(255,255,255,0.14)",
                      background: active ? "rgba(252, 177, 74, 0.16)" : "transparent",
                      color: active ? "#ffd7a8" : "#d2d8e2",
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      padding: "10px 12px",
                      cursor: "pointer",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            aria-label="Filtrar noticias por fuente"
            style={{
              width: "100%",
              background: "#151515",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 10,
              color: "#f5f5f5",
              padding: "10px 12px",
              fontSize: 14,
            }}
          >
            <option value="all">Todas las fuentes</option>
            {availableSources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setDebouncedSearch("");
                setSelectedSource("all");
                setSelectedLanguage("all");
              }}
              style={{
                justifySelf: "start",
                border: "1px solid rgba(255,255,255,0.18)",
                background: "transparent",
                color: "#d7dde7",
                borderRadius: 10,
                padding: "9px 12px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {status === STATUS.LOADING && (
        <div style={{ color: "#aaa", fontSize: 14 }}>Cargando noticias...</div>
      )}

      {status === STATUS.ERROR && (
        <div
          role="alert"
          style={{
            border: "1px solid rgba(255, 107, 107, 0.4)",
            background: "rgba(255, 107, 107, 0.08)",
            borderRadius: 12,
            padding: 14,
            color: "#ffd7d7",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Error al cargar noticias</div>
          <div style={{ color: "#f1c8c8", fontSize: 14 }}>{errorMsg}</div>
          <button
            type="button"
            onClick={triggerRetry}
            disabled={status === STATUS.LOADING || isRetrying}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
              border: "1px solid rgba(252, 177, 74, 0.45)",
              background: "rgba(252, 177, 74, 0.12)",
              color: "#f5d4a2",
              borderRadius: 999,
              padding: "8px 14px",
              fontSize: 13,
              cursor: status === STATUS.LOADING || isRetrying ? "not-allowed" : "pointer",
              opacity: status === STATUS.LOADING || isRetrying ? 0.65 : 1,
            }}
          >
            <RotateCcw size={14} />
            {status === STATUS.LOADING || isRetrying ? "Actualizando..." : "Reintentar"}
          </button>
        </div>
      )}

      {status === STATUS.EMPTY && (
        <div
          style={{
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 12,
            padding: 14,
            color: "#bbb",
            fontSize: 14,
          }}
        >
          {hasActiveFilters
            ? "No hay noticias que coincidan con esa búsqueda o fuente."
            : "No hay noticias disponibles por ahora."}
        </div>
      )}

      {status === STATUS.SUCCESS && (
        <>
          {degradedMsg && (
            <div
              role="status"
              style={{
                border: "1px solid rgba(252, 177, 74, 0.4)",
                background: "rgba(252, 177, 74, 0.08)",
                borderRadius: 12,
                padding: "10px 12px",
                marginBottom: 12,
                color: "#f1d2a0",
                fontSize: 13,
              }}
            >
              {degradedMsg}
            </div>
          )}

          <div className="xp-lets__grid">
            {items.map((item, index) => {
              const theme = themeForSource(item.source);
              const isLead = highlightLead && index === 0 && items.length > 2;
              const dateLabel = item.date ? fmtRelativeDate(item.date) : "Sin fecha";
              const hasImage = Boolean(item.imageUrl);
              const isDashboardCard = dashboardOverlayFixPx > 0;
              const outerShadow = isDashboardCard
                ? "none"
                : isLead
                  ? `0 16px 40px ${theme.glow}`
                  : `0 8px 24px ${theme.glow}`;
              const cardBackground = hasImage
                ? isDashboardCard
                  ? `url("${item.imageUrl}") center / cover no-repeat`
                  : [
                      `linear-gradient(180deg, rgba(4,7,12,0.20) 0%, rgba(4,7,12,0.62) 34%, rgba(4,7,12,0.95) 100%)`,
                      `radial-gradient(circle at 18% 12%, ${theme.bgTop} 0%, rgba(6,8,12,0) 45%)`,
                      `url("${item.imageUrl}") center / cover no-repeat`,
                    ].join(", ")
                : `linear-gradient(160deg, ${theme.bgTop} 0%, rgba(16,16,20,0.88) 56%, ${theme.bgBottom} 100%)`;
              const dashboardOverlayBg =
                isDashboardCard && hasImage
                  ? "linear-gradient(180deg, rgba(4,7,12,0.28) 0%, rgba(4,7,12,0.78) 42%, rgba(4,7,12,0.96) 72%, rgba(4,7,12,1) 100%)"
                  : "none";
              const dashboardInsetShadow =
                isDashboardCard && hasImage
                  ? `inset 0 -${Math.max(16, dashboardOverlayFixPx * 2)}px ${Math.max(
                      26,
                      dashboardOverlayFixPx * 3
                    )}px rgba(4,7,12,0.96), inset 0 -1px 0 rgba(4,7,12,1)`
                  : "none";

              return (
                <article
                  key={item.id}
                  className="xp-card"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handleClick(e, item)}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={(e) => handleTouchEnd(e, item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openItem(item);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    gridColumn: isLead ? "span 2" : "auto",
                    minHeight: isLead ? 190 : 162,
                    borderColor: theme.border,
                    background: cardBackground,
                    backgroundColor: isDashboardCard ? "#050910" : undefined,
                    boxShadow: `${outerShadow}${dashboardInsetShadow !== "none" ? `, ${dashboardInsetShadow}` : ""}`,
                    overflow: "hidden",
                    isolation: "isolate",
                    backfaceVisibility: "hidden",
                  }}
                >
                  {dashboardOverlayBg !== "none" && (
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        inset: "-2px -2px -3px -2px",
                        borderRadius: "inherit",
                        background: dashboardOverlayBg,
                        pointerEvents: "none",
                        zIndex: 0,
                      }}
                    />
                  )}

                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          borderRadius: 999,
                          background: "rgba(0,0,0,0.34)",
                          border: `1px solid ${theme.border}`,
                          color: "#d7e3f3",
                          fontSize: 12,
                          fontWeight: 700,
                          maxWidth: "78%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span
                          className="xp-card__icon"
                          aria-hidden="true"
                          style={{
                            width: 24,
                            height: 24,
                            marginBottom: 0,
                            background: "rgba(0,0,0,0.28)",
                            color: theme.accent,
                          }}
                        >
                          <Newspaper size={14} />
                        </span>
                        {item.source}
                      </div>
                      <div style={{ fontSize: 12, color: "#c9d3e3", whiteSpace: "nowrap" }}>{dateLabel}</div>
                    </div>

                    <h3 className="xp-card__h" style={{ fontSize: isLead ? "1.16rem" : "1rem" }}>
                      {item.title}
                    </h3>

                    <p
                      className="xp-card__p"
                      style={{
                        marginTop: 8,
                        marginBottom: 0,
                        display: "-webkit-box",
                        WebkitLineClamp: isLead ? 4 : 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.snippet || ""}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
